import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, Check, X, ShieldCheck, Loader2, ExternalLink,
} from "lucide-react";

/**
 * AdminUcasQueue — hand-fix queue for UK degrees whose UCAS course
 * code isn't yet live-verified against UCAS' own search index.
 *
 * One row per degree. Editor's choices:
 *   - Approve the verifier's stored suggestion (one click)
 *   - Enter a code manually
 *   - Dismiss (row stays uncoded — used for Oxbridge / providers
 *     that opted out of UCAS' discovery index, so the queue empties
 *     over time to only the truly-fixable rows).
 *
 * Admin-only (backend gated by require_admin). Reachable at
 * `/admin/ucas`; links from AdminHome once shipped there.
 */
export default function AdminUcasQueue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [onlyWithSuggestion, setOnlyWithSuggestion] = useState(true);
  const [manualCodes, setManualCodes] = useState({}); // degree_id -> typed
  const [busy, setBusy] = useState({});               // degree_id -> boolean
  const [bulkBusy, setBulkBusy] = useState(false);
  // Preview state — populated on demand by hitting the bulk endpoint
  // with dry_run=true. Editors can uncheck rows before hitting Commit.
  const [preview, setPreview] = useState(null); // {candidates: [...], ...}
  const [skipIds, setSkipIds] = useState(new Set());
  const [skip, setSkip] = useState(0);
  const PAGE = 100;

  const openBulkPreview = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        "/admin/ucas/queue/bulk-approve?count=500&min_title_sim=0.90&dry_run=true"
      );
      setPreview(data);
      setSkipIds(new Set()); // reset any prior unchecks
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const commitBulkPreview = async () => {
    if (!preview) return;
    const remaining = preview.candidates.length - skipIds.size;
    if (!window.confirm(
      `Commit ${remaining} UCAS suggestions?\n\n`
      + "Every row will be stamped admin-bulk-approve for audit."
    )) return;
    setBulkBusy(true);
    try {
      const skipCsv = Array.from(skipIds).join(",");
      const { data } = await api.post(
        `/admin/ucas/queue/bulk-approve?count=500&min_title_sim=0.90&skip_ids=${encodeURIComponent(skipCsv)}`
      );
      toast.success(`Bulk-approved ${data.applied} rows`);
      setPreview(null);
      setSkipIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSkip = (id) => {
    setSkipIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/ucas/queue", {
        params: {
          limit: PAGE,
          skip,
          only_with_suggestion: onlyWithSuggestion,
        },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [onlyWithSuggestion, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (row, action) => {
    setBusy((b) => ({ ...b, [row.degree_id]: true }));
    try {
      const body = {
        degree_id: row.degree_id,
        action,
        code: action === "set_code" ? (manualCodes[row.degree_id] || "") : undefined,
      };
      const { data: resp } = await api.post("/admin/ucas/queue/decide", body);
      const msg =
        action === "approve_suggestion" || action === "set_code"
          ? `${row.title}: code set to ${resp.code} ✓`
          : `${row.title}: dismissed`;
      toast.success(msg);
      // Optimistically remove the row.
      setData((d) => ({
        ...d,
        rows: d.rows.filter((r) => r.degree_id !== row.degree_id),
        returned_count: (d.returned_count || 1) - 1,
        totals: {
          ...(d.totals || {}),
          in_queue: Math.max(0, (d.totals?.in_queue || 1) - 1),
          live_verified:
            action === "dismiss"
              ? d.totals?.live_verified
              : (d.totals?.live_verified || 0) + 1,
        },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, [row.degree_id]: false }));
    }
  };

  const t = data?.totals;
  const pct = t?.all_uk_degrees
    ? Math.round((t.live_verified / t.all_uk_degrees) * 100)
    : null;

  return (
    <div data-testid="admin-ucas-queue-page" className="max-w-6xl mx-auto px-6 py-8">

      {/* Bulk-approve preview dialog — floats above the queue as a
          modal-lite overlay. Editor can uncheck any row before
          hitting Commit. Uses skipIds set for optimistic client-side
          state; the actual skip is enforced server-side via the
          `skip_ids` query param when committing. */}
      {preview && (
        <div
          data-testid="admin-ucas-bulk-preview"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-editorial text-2xl">Bulk approve preview</h2>
                  <p className="mt-1 text-sm text-stone-600">
                    {preview.candidate_count} rows ≥ 0.90 title similarity.
                    Uncheck any row you'd like to keep in the queue instead.
                  </p>
                </div>
                <button
                  onClick={() => setPreview(null)}
                  data-testid="admin-ucas-bulk-preview-close"
                  className="text-stone-400 hover:text-stone-700 p-1"
                  aria-label="Close preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-3 text-[11px] font-underground-sc tracking-wider text-stone-500">
                Will apply <span className="text-emerald-700 font-semibold">{preview.candidate_count - skipIds.size}</span> ·
                skipped {skipIds.size} · scanned {preview.processed} · low-sim {preview.skipped_low_sim}
              </div>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-stone-100">
              {preview.candidates.map((c) => (
                <li
                  key={c.degree_id}
                  data-testid={`admin-ucas-bulk-preview-row-${c.degree_id}`}
                  className={`p-3 flex items-center gap-4 hover:bg-stone-50 ${skipIds.has(c.degree_id) ? "opacity-50 line-through" : ""}`}
                >
                  <Checkbox
                    checked={!skipIds.has(c.degree_id)}
                    onCheckedChange={() => toggleSkip(c.degree_id)}
                    data-testid={`admin-ucas-bulk-preview-toggle-${c.degree_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      <span className="font-medium">{c.title}</span>
                      <span className="text-stone-500"> · {c.university_name}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] font-mono text-stone-500 truncate">
                      matches: {c.matched_title}
                    </div>
                  </div>
                  <div className="text-sm font-mono flex items-center gap-2 shrink-0">
                    <span className="text-stone-400">{c.old_code || "—"}</span>
                    <span aria-hidden>→</span>
                    <span className="font-semibold text-emerald-700">{c.new_code}</span>
                  </div>
                  <span className="text-[11px] font-mono text-stone-500 shrink-0 w-14 text-right">
                    {(c.similarity * 100).toFixed(0)}%
                  </span>
                </li>
              ))}
            </ul>
            <div className="p-4 border-t border-stone-100 flex items-center justify-between">
              <span className="text-xs text-stone-500">Commit is audit-stamped as admin-bulk-approve.</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setPreview(null)} data-testid="admin-ucas-bulk-preview-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={commitBulkPreview}
                  disabled={bulkBusy || preview.candidate_count - skipIds.size === 0}
                  data-testid="admin-ucas-bulk-preview-commit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {bulkBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                  Commit {preview.candidate_count - skipIds.size} rows
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]">
        <ArrowLeft className="h-3 w-3" /> Admin home
      </Link>
      <h1 className="mt-4 font-editorial text-3xl sm:text-4xl">
        UCAS manual-fix queue
      </h1>

      {t && (
        <div
          data-testid="admin-ucas-queue-totals"
          className="mt-4 flex flex-wrap items-center gap-3 text-xs font-underground-sc tracking-wider text-stone-600"
        >
          <span className="rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1">
            <ShieldCheck className="h-3 w-3 inline mr-1" />
            {t.live_verified.toLocaleString()} of {t.all_uk_degrees.toLocaleString()} UK degrees UCAS-verified
            {pct != null && <span className="ml-1">· {pct}%</span>}
          </span>
          <span className="rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1">
            {t.in_queue.toLocaleString()} still open
          </span>
        </div>
      )}

      <div className="mt-6 flex items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
          <Checkbox
            checked={onlyWithSuggestion}
            onCheckedChange={(v) => {
              setSkip(0);
              setOnlyWithSuggestion(!!v);
            }}
            data-testid="admin-ucas-only-suggestions-toggle"
          />
          Only rows with a UCAS suggestion (fastest wins first)
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={openBulkPreview}
          disabled={bulkBusy}
          data-testid="admin-ucas-bulk-approve"
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          title="Opens a preview dialog so you can veto rows before committing. Every row keeps a title similarity ≥ 0.90."
        >
          {bulkBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
          Preview bulk approve (top 500)
        </Button>
        <div className="ml-auto flex items-center gap-2 text-xs text-stone-500">
          <Button
            variant="outline"
            size="sm"
            disabled={skip === 0}
            onClick={() => setSkip((s) => Math.max(0, s - PAGE))}
          >
            ← Prev
          </Button>
          <span className="font-mono">
            {skip + 1}–{skip + (data?.returned_count || 0)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!data || data.returned_count < PAGE}
            onClick={() => setSkip((s) => s + PAGE)}
          >
            Next →
          </Button>
        </div>
      </div>

      {err && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
          {err}
        </div>
      )}

      {loading && (
        <div className="mt-8 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && data?.rows && (
        <ul className="mt-6 divide-y divide-stone-100 rounded-lg border border-stone-200 bg-white overflow-hidden">
          {data.rows.map((r) => (
            <li key={r.degree_id} data-testid={`admin-ucas-row-${r.degree_id}`} className="p-4">
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/degree/${r.degree_id}`}
                    target="_blank"
                    className="font-editorial text-lg text-stone-900 hover:text-[#4C1D95] inline-flex items-center gap-1"
                  >
                    {r.title}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                  <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mt-0.5">
                    {r.university_name}
                    {r.ucas_institution_code && (
                      <span className="text-stone-400"> · Inst {r.ucas_institution_code}</span>
                    )}
                    {r.qualification && <span className="text-stone-400"> · {r.qualification}</span>}
                    {r.campus && <span className="text-stone-400"> · {r.campus}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-mono">
                    <span className="text-stone-500">Current:</span>
                    <span className={r.current_code ? "text-stone-800" : "text-stone-400 italic"}>
                      {r.current_code || "(none)"}
                    </span>
                    {r.current_source && (
                      <span className="text-stone-400">via {r.current_source}</span>
                    )}
                    {r.provider_on_ucas === false && (
                      <span className="text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                        Provider not on UCAS
                      </span>
                    )}
                  </div>
                </div>

                <div className="w-full md:w-auto flex flex-wrap items-center gap-3 shrink-0">
                  {r.suggested_code ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                        UCAS suggests
                      </span>
                      <span
                        className="font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-sm"
                        title={r.suggested_title || ""}
                        data-testid={`admin-ucas-row-${r.degree_id}-suggested`}
                      >
                        {r.suggested_code}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => decide(r, "approve_suggestion")}
                        disabled={busy[r.degree_id]}
                        data-testid={`admin-ucas-row-${r.degree_id}-approve`}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Approve
                      </Button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-stone-400 italic">
                      No suggestion — enter manually
                    </span>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Input
                      placeholder="e.g. L100"
                      value={manualCodes[r.degree_id] || ""}
                      onChange={(e) =>
                        setManualCodes((m) => ({ ...m, [r.degree_id]: e.target.value.toUpperCase() }))
                      }
                      data-testid={`admin-ucas-row-${r.degree_id}-manual-input`}
                      className="w-24 h-8 text-sm font-mono"
                      maxLength={8}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decide(r, "set_code")}
                      disabled={
                        busy[r.degree_id] ||
                        !(manualCodes[r.degree_id]?.length >= 3)
                      }
                      data-testid={`admin-ucas-row-${r.degree_id}-set`}
                    >
                      Set
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => decide(r, "dismiss")}
                    disabled={busy[r.degree_id]}
                    data-testid={`admin-ucas-row-${r.degree_id}-dismiss`}
                    className="text-stone-500 hover:text-red-700"
                  >
                    <X className="h-3 w-3 mr-1" /> Dismiss
                  </Button>
                </div>
              </div>
              {r.suggested_title && (
                <div className="mt-1 pl-1 text-[11px] text-stone-500 italic">
                  UCAS title match: “{r.suggested_title}”
                </div>
              )}
            </li>
          ))}
          {data.rows.length === 0 && (
            <li className="p-8 text-center text-stone-500 text-sm">
              {onlyWithSuggestion
                ? "No rows with suggestions on this page. Try turning the filter off, or advance to the next page."
                : "Nothing to review — the queue is empty on this page."}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
